import React, { useEffect, useMemo, useState } from 'react';
import { WhatsAppTemplate } from '../../types';
import { getWhatsAppTemplates } from '../../services/marketingApi';
import { Cadence, cadenceApi } from '../../services/coreApi';
import CadenceEditor from './CadenceEditor';
import { Empty, Failed, Loading, Note, PageFrame, Pill, Section, Th, btn, btnTone, td, trClick } from '../../components/Kit';

/** Cadences — the sequences of templates at T + days + hours. A cadence has
 *  no status: attach makes it send, detach stops it (MAG-76). This page lists
 *  them and opens the editor; numbers here are leads, never sends — sends are
 *  on the campaign portal. */

const fmtWhen = new Intl.DateTimeFormat('en-IN', { timeZone: 'Asia/Kolkata', day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit', hour12: false });

const Cadences: React.FC = () => {
  const [cadences, setCadences] = useState<Cadence[] | null>(null);
  const [templates, setTemplates] = useState<WhatsAppTemplate[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<{ open: boolean; cadence: Cadence | null }>({ open: false, cadence: null });

  const load = async () => {
    try {
      const [c, t] = await Promise.all([cadenceApi.list(), getWhatsAppTemplates({ include_retired: true })]);
      setCadences(c);
      setTemplates(t.templates);
    } catch (err: any) {
      setError(err?.response?.data?.detail || err.message);
    }
  };
  useEffect(() => { load(); }, []);

  const rows = useMemo(() => (cadences || []).slice().sort((a, b) => b.updated_at.localeCompare(a.updated_at)), [cadences]);

  return (
    <PageFrame
      title="Cadences"
      subline="Templates sent at T + days + hours after a lead is attached."
      action={<button type="button" onClick={() => setEditing({ open: true, cadence: null })} className={`${btn} ${btnTone.forest}`}>+ New cadence</button>}
    >
      {error ? <Failed what="cadences" error={error} /> : !cadences ? <Loading what="cadences" /> : (
        <Section title="All cadences" note="Counts are leads, not sends">
          <table className="w-full border-collapse">
            <thead><tr>
              <Th left>Cadence</Th><Th>Steps</Th><Th>Enrolled now</Th><Th>Completed</Th><Th left>Last edited</Th>
            </tr></thead>
            <tbody>
              {rows.length === 0 && <Empty cols={5}>No cadences yet.</Empty>}
              {rows.map((c) => (
                <tr key={c.id} onClick={() => setEditing({ open: true, cadence: c })} className={trClick()}>
                  <td className={`${td} font-semibold text-charcoal`}>{c.name}</td>
                  <td className={`${td} text-right`}>{c.steps.length}</td>
                  <td className={`${td} text-right`}>
                    <span className={c.enrolled ? 'font-semibold text-forest' : ''}>{c.enrolled.toLocaleString('en-IN')}</span>
                    {c.held > 0 && <span className="ml-2"><Pill tone="gold">{c.held} held</Pill></span>}
                  </td>
                  <td className={`${td} text-right`}>{c.completed.toLocaleString('en-IN')}</td>
                  <td className={`${td} text-[12.5px] text-graphite`}>{fmtWhen.format(new Date(c.updated_at))}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <Note>Edits reach every enrolled lead who hasn't had that step yet.</Note>
        </Section>
      )}

      {editing.open && templates && (
        <CadenceEditor
          cadence={editing.cadence}
          templates={templates}
          onClose={() => setEditing({ open: false, cadence: null })}
          onSaved={() => { setEditing({ open: false, cadence: null }); load(); }}
        />
      )}
    </PageFrame>
  );
};

export default Cadences;
